import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Loader2, RefreshCw, Trash2, X } from "lucide-react";
import { createApiKey, revokeApiKey } from "../../../services/apiKeyService";
import {
  DEFAULT_EXPIRY,
  ErrorBox,
  ExpiryField,
  NEVER_EXPIRES,
  NewKeyResult,
  REVOKE_REASON_MAX_LENGTH,
  expiryToDays,
  formatDateTime,
  keyLabel,
  lastUsedText,
  listMayBeStale
} from "./apiKeyParts";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Focus target when a dialog closes after its opener was removed. */
export const API_KEY_LIST_ID = "api-key-list";

/**
 * Modal shell. While `busy` (a request is in flight) it cannot be closed: the
 * response may carry a one-time raw key that would be lost with the dialog.
 * Focus moves into the dialog on open (to `[data-autofocus]` if present),
 * Tab is kept inside, Escape closes when not busy, and focus returns to the
 * element that opened it.
 */
function DialogFrame({
  titleId,
  icon: Icon,
  tone,
  title,
  subtitle,
  busy = false,
  onClose,
  children
}) {
  const dialogRef = useRef(null);
  const busyRef = useRef(busy);
  const onCloseRef = useRef(onClose);
  busyRef.current = busy;
  onCloseRef.current = onClose;

  const requestClose = () => {
    if (!busyRef.current) onCloseRef.current?.();
  };

  useEffect(() => {
    const opener = document.activeElement;
    const node = dialogRef.current;
    const target = node?.querySelector("[data-autofocus]") || node;
    target?.focus();

    // On the document, so Escape and the Tab trap still work when the focused
    // element inside the dialog is replaced (e.g. the form by the new key).
    const onKeyDown = (e) => {
      const dialog = dialogRef.current;
      if (!dialog) return;
      if (e.key === "Escape") {
        if (!busyRef.current) onCloseRef.current?.();
        return;
      }
      if (e.key !== "Tab") return;
      const nodes = Array.from(dialog.querySelectorAll(FOCUSABLE));
      if (nodes.length === 0) {
        e.preventDefault();
        dialog.focus();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;
      if (!dialog.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && (active === first || active === dialog)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (opener && typeof opener.focus === "function" && opener.isConnected) {
        opener.focus();
      } else {
        // The opener can disappear with the change it made (a rotated key loses
        // its Rotate button): land on the key list rather than the page body.
        document.getElementById(API_KEY_LIST_ID)?.focus();
      }
    };
  }, []);

  const toneClass =
    tone === "danger"
      ? "from-red-500 to-rose-500"
      : "from-blue-500 to-indigo-500";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-busy={busy || undefined}
        tabIndex={-1}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto focus:outline-none"
      >
        <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-3">
          <div
            className={`w-9 h-9 rounded-lg bg-gradient-to-br ${toneClass} flex items-center justify-center text-white shrink-0`}
          >
            <Icon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 id={titleId} className="font-bold text-gray-900">
              {title}
            </h2>
            {subtitle ? (
              <p className="text-xs text-gray-500 truncate">{subtitle}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={requestClose}
            disabled={busy}
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function KeyIdentity({ keyItem }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
      <div className="font-medium text-gray-900 break-words">
        {keyItem.name}
      </div>
      {keyItem.created_at ? (
        <div className="text-xs text-gray-600">
          Created {formatDateTime(keyItem.created_at)}
        </div>
      ) : null}
      <div className="text-xs text-gray-500 mt-0.5">
        {lastUsedText(keyItem)}
      </div>
    </div>
  );
}

const GRACE_OPTIONS = [
  {
    value: "24",
    label: "Keep the old key working for 24 hours (planned rotation)",
    hint: "Move your integrations to the new key within 24 hours; the old key then stops working."
  },
  {
    value: "0",
    label: "Revoke the old key immediately (key compromised)",
    hint: "Anything still using the old key gets 401 errors right away."
  }
];

/**
 * Rotate: mint a replacement for `keyItem` (additive_v1 only) and either
 * retire the old key after 24 hours or revoke it now.
 */
export function RotateKeyDialog({
  keyItem,
  lookup,
  onClose,
  onRotated,
  onStale
}) {
  const [grace, setGrace] = useState("");
  const [expiry, setExpiry] = useState(DEFAULT_EXPIRY);
  const [neverAck, setNeverAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const needsAck = expiry === NEVER_EXPIRES && !neverAck;
  const canSubmit = grace !== "" && !needsAck && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError("");
    // Snapshot labels now: after the refresh a revoked key leaves the list.
    const snapshot = { ...(lookup || {}), [keyItem.id]: keyLabel(keyItem) };
    const res = await createApiKey({
      name: keyItem.name,
      expires_in_days: expiryToDays(expiry),
      replaces_key_id: keyItem.id,
      grace_hours: Number(grace)
    });
    setBusy(false);
    if (!res.success) {
      setError(res.message);
      // 404/409: the key is gone or already replaced; timeout/5xx: the
      // rotation may have happened. Either way the list needs a refresh.
      if (listMayBeStale(res, { keyScoped: true })) onStale?.();
      return;
    }
    if (!res.data?.raw_key) {
      setError(
        "The server did not return the new key. Refresh the list and check the key's status."
      );
      onRotated?.();
      return;
    }
    setResult({ data: res.data, lookup: snapshot });
    onRotated?.(res.data);
  };

  return (
    <DialogFrame
      titleId="rotate-key-title"
      icon={RefreshCw}
      title={result ? "Key rotated" : `Rotate key “${keyItem.name}”`}
      subtitle={result ? null : "Creates a replacement key with the same name."}
      busy={busy}
      onClose={onClose}
    >
      {result ? (
        <div className="p-5">
          <NewKeyResult
            result={result.data}
            lookup={result.lookup}
            onDone={onClose}
          />
        </div>
      ) : (
        <form onSubmit={submit}>
          <div className="p-5 space-y-4">
            <KeyIdentity keyItem={keyItem} />

            <fieldset>
              <legend className="block text-sm font-medium text-gray-700 mb-1.5">
                What should happen to the old key?
              </legend>
              <div className="space-y-2">
                {GRACE_OPTIONS.map((option, index) => (
                  <label
                    key={option.value}
                    className={`flex items-start gap-2 rounded-lg border p-3 text-sm cursor-pointer ${
                      grace === option.value
                        ? "border-blue-400 bg-blue-50"
                        : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="rotate-grace"
                      value={option.value}
                      checked={grace === option.value}
                      onChange={(e) => setGrace(e.target.value)}
                      disabled={busy}
                      data-autofocus={index === 0 ? true : undefined}
                      className="mt-0.5"
                    />
                    <span>
                      <span className="block text-gray-900">
                        {option.label}
                      </span>
                      <span className="block text-xs text-gray-500">
                        {option.hint}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            <ExpiryField
              id="rotate-key-expiry"
              value={expiry}
              onChange={(value) => {
                setExpiry(value);
                setNeverAck(false);
              }}
              ack={neverAck}
              onAckChange={setNeverAck}
              disabled={busy}
            />

            <ErrorBox message={error} />
          </div>

          <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Rotate key
            </button>
          </div>
        </form>
      )}
    </DialogFrame>
  );
}

/** Revoke: confirm, optional reason, POST /revoke. */
export function RevokeKeyDialog({ keyItem, onClose, onRevoked, onStale }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    const res = await revokeApiKey(keyItem.id, reason);
    setBusy(false);
    if (!res.success) {
      setError(res.message);
      // 404: the key is not there any more; timeout/5xx: the revoke may have
      // been applied. Refresh so the list stops offering stale actions.
      if (listMayBeStale(res, { keyScoped: true })) onStale?.();
      return;
    }
    onRevoked?.(res.data || null, keyItem);
  };

  return (
    <DialogFrame
      titleId="revoke-key-title"
      icon={Trash2}
      tone="danger"
      title={`Revoke key “${keyItem.name}”?`}
      subtitle="This cannot be undone."
      busy={busy}
      onClose={onClose}
    >
      <form onSubmit={submit}>
        <div className="p-5 space-y-4">
          <KeyIdentity keyItem={keyItem} />

          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              Anything using this key gets <strong>401</strong> errors
              immediately after you revoke it.
            </span>
          </div>

          <div>
            <label
              htmlFor="revoke-key-reason"
              className="block text-sm font-medium text-gray-700 mb-1.5"
            >
              Reason (optional)
            </label>
            <textarea
              id="revoke-key-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              maxLength={REVOKE_REASON_MAX_LENGTH}
              disabled={busy}
              data-autofocus
              aria-describedby="revoke-key-reason-count"
              placeholder="e.g. Key was shared in a support ticket"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p
              id="revoke-key-reason-count"
              className="mt-1 text-right text-xs text-gray-400"
            >
              {reason.length}/{REVOKE_REASON_MAX_LENGTH}
            </p>
          </div>

          <ErrorBox message={error} />
        </div>

        <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
            Revoke key
          </button>
        </div>
      </form>
    </DialogFrame>
  );
}
